import { CopyOutlined, DeleteOutlined, EditOutlined, PlusOutlined, ReloadOutlined } from "@ant-design/icons"
import type { TableColumnsType, TablePaginationConfig, TableProps } from "antd"
import { Button, Card, message, Modal, Select, Space, Table, Tabs, Tooltip } from "antd"
import Title from "antd/es/typography/Title"
import queryString from "query-string"
import React, { useCallback, useEffect, useRef, useState } from "react"
import { useSelector } from "react-redux"
import { useNavigate } from "react-router"
import { Link } from "react-router-dom"
import Filters, { FilterComponent } from "~/components/Filters"
import { Paths } from "~/constants"
import { t } from "~/languages/i18n"
import { JobDetailAttributes, JobDetailData, PaginationMeta } from "~/types/types"
import { formatDatePost } from "~/utils/formatDate"
import { normalizeText } from "~/utils/helper"
import { RootState } from "../../auth/services/redux/store"
import {
  addJobDetail,
  checkSlugJobDetail,
  deleteJobDetail,
  fetchJobDetailId,
  fetchJobDetails,
} from "../service/jobDetail"

const List: React.FC = () => {
  const [dataItem, setDataItem] = useState<JobDetailData[]>([])
  const filterRef = useRef<{ filterFunction: () => void } | null>(null)
  const [sortPaginationValues, setSortPaginationValues] = useState<{ [key: string]: string } | null>(null)
  const [needRefresh, setNeedRefresh] = useState<boolean>(false)
  const [filterValues, setFilterValues] = useState<{ [key: string]: string } | null>(null)
  const [defaultFilterValues, setDefaultFilterValues] = useState<{ [key: string]: any } | null>(null)
  const [jobDetailNameOptions, setJobDetailNameOptions] = useState<{ value: string | number; label: string }[]>([])
  const [filterTabPost, setFilterTabPost] = useState("all")
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
  const [bulkAction, setBulkAction] = useState<string>("")
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [metaData, setMetaData] = useState<PaginationMeta>()
  const [totalItem, setTotalItem] = useState(0)
  const permissions = JSON.parse(localStorage.getItem("permissions") || "{}")

  const [jobDetailItemData, setJobDetailItemData] = useState<JobDetailData>()
  const account = useSelector((state: RootState) => state.account)
  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedRowKeys: React.Key[]) => {
      setSelectedRowKeys(selectedRowKeys)
    },
  }

  const fetchJobDetailsData = async (searchTerms?: { [key: string]: string }) => {
    setLoading(true)
    setDataItem([])
    try {
      const defaultPagination = {
        page: "1",
        pageSize: "20",
      }
      const response = await fetchJobDetails({ ...defaultPagination, ...searchTerms })
      const meta = response && response.meta

      setMetaData(meta)

      if (response && response.length > 0) {
        setDataItem(response)

        if (searchTerms?.publicationState === "all") {
          setTotalItem(meta?.pagination?.total || 0)
        }
        setJobDetailNameOptions(
          response.map((jobDetail: { id: number; attributes: JobDetailAttributes }) => ({
            value: jobDetail.id,
            label: jobDetail.attributes.JobTitle,
          })),
        )

        setLoading(false)
      } else {
        setLoading(false)
      }

      return response
    } catch (error) {
      message.error(t("Failed to fetch job details. Please try again."))
    }
  }

  const onSearch = useCallback(
    (filters: object) => {
      const values: { [key: string]: string } = {}

      Object.keys(filters).forEach(key => {
        const value = filters[key as keyof typeof filters]
        if (typeof value === "string") {
          values[key] = value
        }
      })

      if (!values) {
        message.error("Values are undefined")
        return
      }

      let newValue = { ...values }

      if (values.search) {
        newValue = { ...newValue, search: normalizeText(values.search) }
      }

      const searchParams = queryString.stringify(newValue, { encode: false })
      const newUrl = searchParams.length > 0 ? `${location.pathname}?${searchParams}` : location.pathname
      navigate(newUrl)
    },
    [navigate],
  )

  useEffect(() => {
    const searchParams = queryString.parse(location.search)
    const filterValues: { [key: string]: string } = {}
    const sortPaginationValues: { [key: string]: string } = {}

    for (const key of Object.keys(searchParams)) {
      const value = searchParams[key]
      if (value && typeof value === "string") {
        if (key === "page" || key === "limit" || value === "asc" || value === "desc") {
          sortPaginationValues[key] = value
        } else {
          filterValues[key] = value
        }
      }
    }

    setSortPaginationValues(sortPaginationValues)
    setFilterValues(filterValues)
    setDefaultFilterValues(filterValues)
  }, [location.search])

  useEffect(() => {
    if (!filterValues && !sortPaginationValues) return

    if (filterValues || sortPaginationValues) {
      const params = { ...filterValues, ...sortPaginationValues, publicationState: filterTabPost }
      fetchJobDetailsData(params)
    }
  }, [filterValues, sortPaginationValues, needRefresh])

  const handleEdit = (id: number) => {
    navigate(`${id}`)
  }

  const getJobDetailInformation = async (id: number) => {
    try {
      const res = await fetchJobDetailId(id)
      setJobDetailItemData(res)
    } catch (error) {
      message.error(t("Fetch job detail error!"))
    }
  }
  const isSlugUnique = async (slug: string): Promise<boolean> => {
    try {
      const response = await checkSlugJobDetail(slug)
      return response.data.length === 0
    } catch {
      return false
    }
  }

  const generateUniqueSlug = async (
    baseSlug: string,
    baseJobTitle: string,
  ): Promise<{ slug: string; jobTitle: string }> => {
    let newSlug = baseSlug
    let newJobTitle = baseJobTitle
    let count = 1

    const match = newSlug.match(/-copy-(\d+)$/)
    if (match) {
      count = parseInt(match[1], 10) + 1
      newSlug = `${baseSlug.replace(/-copy-(\d+)$/, "")}-copy-${count}`
      newJobTitle = `${baseJobTitle.replace(/-copy-(\d+)$/, "")}-copy-${count}`
    } else {
      newSlug = `${baseSlug}-copy-1`
      newJobTitle = `${baseJobTitle}-copy-1`
    }

    while (!(await isSlugUnique(newSlug))) {
      count++
      newSlug = `${baseSlug.replace(/-copy-\d*$/, "")}-copy-${count}`
      newJobTitle = `${baseJobTitle.replace(/-copy-\d*$/, "")}-copy-${count}`
    }

    return { slug: newSlug, jobTitle: newJobTitle }
  }

  useEffect(() => {
    if (jobDetailItemData) {
      handleDuplicate()
    }
  }, [jobDetailItemData])
  const handleDuplicate = async () => {
    if (jobDetailItemData) {
      const baseSlug = jobDetailItemData.attributes.Slug
      const baseJobTitle = jobDetailItemData.attributes.JobTitle
      const { slug: uniqueSlug, jobTitle: uniqueJobTitle } = await generateUniqueSlug(baseSlug, baseJobTitle)

      const dataPayload = {
        ...jobDetailItemData.attributes,
        Slug: uniqueSlug,
        JobTitle: uniqueJobTitle,
        publishedAt: new Date().toISOString(),
      }

      try {
        await addJobDetail({ data: dataPayload })
        message.success({ content: t("Job detail duplicated successfully!"), duration: 2 })
        setJobDetailItemData(undefined)
        fetchJobDetailsData()
      } catch (error) {
        message.error(t("Failed to duplicate job detail. Please try again."))
      }
    }
  }

  const handleDelete = async (id: number) => {
    Modal.confirm({
      title: t("Confirm Delete"),
      content: t("Are you sure you want to delete this job detail?"),
      okText: t("Delete"),
      cancelText: t("Cancel"),
      onOk: async () => {
        try {
          setLoading(true)
          await deleteJobDetail(id)
          fetchJobDetailsData()
          message.success(t("Job detail deleted successfully!"))
        } catch {
          message.error(t("Handle Delete Error"))
        }
      },
    })
  }

  const handleBulkDelete = async () => {
    try {
      setLoading(true)
      const ids = selectedRowKeys.map(id => Number(id))
      await Promise.all(ids.map(id => deleteJobDetail(id)))
      setSelectedRowKeys([])
      fetchJobDetailsData()
      setLoading(false)
      message.success(t("Job details deleted successfully"))
    } catch {
      message.error(t("Handle Delete Error"))
    }
  }

  const handleApplyBulkAction = () => {
    if (bulkAction === "delete" && selectedRowKeys.length > 0) {
      Modal.confirm({
        title: t("Confirm Delete"),
        content: t("Are you sure you want to delete the selected job details?"),
        okText: t("Delete"),
        cancelText: t("Cancel"),
        onOk: handleBulkDelete,
      })
    } else if (selectedRowKeys.length === 0) {
      message.warning(t("Please select job details to apply the action."))
    } else {
      message.warning(t("Please select a valid bulk action."))
    }
  }

  const handleChangeTab = (key: string) => {
    setFilterTabPost(key)
    if (filterRef.current) {
      filterRef.current.filterFunction()
    }
    setSortPaginationValues(null)
    fetchJobDetailsData({
      ...filterValues,
      ...sortPaginationValues,
      publicationState: key,
    })
  }

  const onChange: TableProps<JobDetailData>["onChange"] = (pagination: TablePaginationConfig) => {
    setSortPaginationValues({
      page: pagination.current ? pagination.current.toString() : "1",
      pageSize: pagination.pageSize?.toString() || "20",
    })
  }

  const onRefresh = () => {
    if (filterRef.current) {
      filterRef.current.filterFunction()
    }
    setSortPaginationValues(null)
    setNeedRefresh(!needRefresh)
  }

  const columns: TableColumnsType<JobDetailData> = [
    {
      title: t("Job Title"),
      dataIndex: ["attributes", "JobTitle"],
      key: "JobTitle",
      showSorterTooltip: false,
      sorter: (a, b) => a.attributes.JobTitle.localeCompare(b.attributes.JobTitle),
      width: "20%",
      render: (_, record) => (
        <Link
          to={`${Paths.JOB_DETAIL.EDIT.PATH}/${record.id}`}
          className="text-[#1677ff] cursor-pointer transition duration-300 hover:text-[#0b5ed7] hover:underline text-base"
        >
          {record.attributes.JobTitle}
        </Link>
      ),
    },
    {
      title: t("Location"),
      dataIndex: ["attributes", "Location"],
      key: "Location",
      width: "10%",
      render: (location: string) => {
        return <span className="text-base line-clamp-2">{location}</span>
      },
      showSorterTooltip: false,
      sorter: (a, b) => (a.attributes?.Location || "").localeCompare(b.attributes?.Location || ""),
    },
    {
      title: t("Type"),
      dataIndex: ["attributes", "Type"],
      key: "Type",
      width: "15%",
      render: (type: string) => {
        return <span className="text-base line-clamp-2">{type}</span>
      },
      showSorterTooltip: false,
      sorter: (a, b) => (a.attributes?.Type || "").localeCompare(b.attributes?.Type || ""),
    },
    {
      title: t("Description"),
      dataIndex: ["attributes", "Description"],
      key: "Description",
      width: "30%",
      render: (description: string) => {
        return <span className="text-base line-clamp-2">{description}</span>
      },
      showSorterTooltip: false,
      sorter: (a, b) => (a.attributes?.Description || "").localeCompare(b.attributes?.Description || ""),
    },
    {
      title: t("Create At"),
      dataIndex: ["attributes"],
      key: "createdAt",
      width: "15%",
      render: (data: JobDetailAttributes) => {
        const formattedDate = formatDatePost(data.createdAt || "")
        return (
          <span className="font-normal">
            {t("Last Modified")}
            <br />
            <span>{formattedDate}</span>
          </span>
        )
      },
    },
  ]

  columns.push({
    title: t("Action"),
    key: "action",
    width: "15%",
    render: (_, record) => (
      <Space size="small" className="justify-center items-center">
        <Tooltip title={t("Edit")}>
          <Button
            type="primary"
            icon={<EditOutlined />}
            className="bg-blue-500 px-4 py-2 rounded"
            onClick={() => handleEdit(record.id)}
          />
        </Tooltip>

        <Tooltip title={t("Duplicate")}>
          <Button type="default" icon={<CopyOutlined />} onClick={() => getJobDetailInformation(record.id)} />
        </Tooltip>

        <Tooltip title={t("Delete")}>
          <Button danger type="primary" icon={<DeleteOutlined />} onClick={() => handleDelete(record.id)} />
        </Tooltip>
      </Space>
    ),
  })

  return (
    <div className="p-4 ">
      <div className="flex space-x-4 items-center">
        <Title level={2} className="m-0">
          {t("Job Detail")}
        </Title>
        <Button
          onClick={() => navigate(Paths.JOB_DETAIL.CREATE.PATH)}
          color="primary"
          variant="outlined"
          icon={<PlusOutlined />}
        >
          {t("Post Job Detail")}
        </Button>
      </div>
      <div className="flex justify-between mt-3">
        <Tabs
          defaultActiveKey="all"
          activeKey={filterTabPost}
          onChange={handleChangeTab}
          items={[
            { label: `${t("All")} (${totalItem})`, key: "all" },
            { label: `${t("Draft")}`, key: "draft" },
          ]}
        />
      </div>
      <div className="items-center">
        <Space className=" space-x-5 flex justify-between">
          <Space className="space-x-3">
            <Select
              defaultValue={t("Bulk Actions")}
              style={{ width: 170 }}
              onChange={value => setBulkAction(value)}
              options={[
                { value: "all", label: t("Bulk Actions") },
                ...(permissions.jobDetail?.delete.enabled ? [{ value: "delete", label: t("Delete") }] : []),
              ]}
            />
            <Button onClick={handleApplyBulkAction}>{t("Apply")}</Button>
          </Space>
          <Space>
            <Button onClick={onRefresh} icon={<ReloadOutlined />}>
              {t("Refresh")}
            </Button>
          </Space>
        </Space>
      </div>
      <Card type="inner" style={styles.card}>
        <Filters
          filterRef={filterRef}
          defaultFilterValues={defaultFilterValues}
          onSubmit={onSearch}
          filters={
            [
              {
                label: t("Job Title"),
                placeholder: t("Find Job Detail Name"),
                name: "JobTitle",
                type: "text",
                options: jobDetailNameOptions,
                customStyle: "2xl:w-[20rem] w-[15rem]",
              },
              {
                label: t("Location"),
                placeholder: t("Find Location"),
                name: "Location",
                type: "text",
                customStyle: "2xl:w-[10rem] w-[5rem]",
              },
              {
                label: t("Type"),
                placeholder: t("Find Type"),
                name: "Type",
                type: "text",
                customStyle: "2xl:w-[10rem] w-[5rem]",
              },
            ] as FilterComponent[]
          }
        />
      </Card>
      <Table
        className="mt-3 border border-[rgb(229 229 229)]"
        rowKey="id"
        rowSelection={rowSelection}
        columns={columns}
        loading={loading}
        dataSource={dataItem}
        onChange={onChange}
        pagination={{
          pageSize: 20,
          total: (metaData && metaData?.pagination?.total) || 0,
        }}
        rowClassName={(record, index) => (index % 2 === 0 ? "bg-[#f6f7f7] " : "bg-white")}
      />
    </div>
  )
}

export default List

const styles = {
  title: {
    marginTop: 0,
    marginBottom: 0,
  },
  card: {
    marginTop: "1.25rem",
    marginBottom: "1.25rem",
  },
}
